import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import {
  LayoutDashboard, BookOpen, Archive, MessageSquare, LogOut, Zap, X, BarChart2
} from "lucide-react";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/knowledge", icon: BookOpen, label: "Knowledge Base" },
  { path: "/vault", icon: Archive, label: "Content Vault" },
  { path: "/chat", icon: MessageSquare, label: "AI Chat" },
  { path: "/stats", icon: BarChart2, label: "Stats" },
];

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => { onClose?.(); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={
        "fixed left-0 top-0 h-screen w-64 flex flex-col border-r border-white/10 bg-[#0A0A0C] z-40 transition-transform duration-250 ease-out " +
        "md:translate-x-0 " +
        (isOpen ? "translate-x-0" : "-translate-x-full")
      }>
        {/* Logo + Close on mobile */}
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
              <Zap className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-white tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>BehaviorBase</h1>
              <p className="text-[10px] text-indigo-400" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Learning Hub</p>
            </div>
          </div>
          <button onClick={onClose} className="md:hidden text-[#94A3B8] hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              end={path === "/"}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 " + (
                  isActive
                    ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/20"
                    : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                )
              }
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span style={{ fontFamily: "'Inter', sans-serif" }}>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User + Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
                <span className="text-xs text-indigo-300 font-semibold">{user?.name?.charAt(0) || "A"}</span>
              </div>
              <span className="text-xs text-[#94A3B8]" style={{ fontFamily: "'Inter', sans-serif" }}>{user?.name || "Admin"}</span>
            </div>
            <button onClick={handleLogout} data-testid="logout-button" className="p-1.5 text-[#94A3B8] hover:text-white hover:bg-white/5 rounded-md transition-colors duration-200" title="Logout">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
